import React, { useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { Menu, Icon, Dropdown } from "semantic-ui-react";
import { useIsMobile } from "@utils/mobileUtils";

const COLORS = {
  primary: "#225745",
  white: "#fff",
  textSecondary: "#666",
};

const TabNavigation = ({ activeTab, onTabChange, showLibraryCard }) => {
  const isMobile = useIsMobile();

  const styles = useMemo(
    () => ({
      menu: {
        borderBottom: `2px solid ${COLORS.primary}`,
        backgroundColor: COLORS.white,
        borderRadius: "8px 8px 0 0",
        marginBottom: 0,
      },
      mobileContainer: {
        backgroundColor: COLORS.white,
        borderRadius: "8px 8px 0 0",
        padding: "1rem",
        borderBottom: `2px solid ${COLORS.primary}`,
      },
      dropdown: { border: `1px solid ${COLORS.primary}`, borderRadius: "6px" },
    }),
    []
  );

  const tabs = useMemo(() => {
    const baseTabs = [
      {
        key: "profile",
        name: "profile",
        icon: "user",
        label: isMobile ? "Profile" : "Profile Information",
      },
      {
        key: "password",
        name: "password",
        icon: "key",
        label: isMobile ? "Password" : "Change Password",
      },
    ];
    if (showLibraryCard) {
      baseTabs.push({
        key: "libraryCard",
        name: "libraryCard",
        icon: "id card",
        label: isMobile ? "Card" : "Library Card",
      });
    }
    return baseTabs;
  }, [isMobile, showLibraryCard]);

  const dropdownOptions = useMemo(
    () =>
      tabs.map((tab) => ({
        key: tab.key,
        value: tab.name,
        text: tab.label,
        icon: tab.icon,
      })),
    [tabs]
  );

  const activeTabData = useMemo(
    () => tabs.find((tab) => tab.name === activeTab) || tabs[0],
    [tabs, activeTab]
  );

  const getItemStyle = useCallback(
    (tabName) => ({
      color: activeTab === tabName ? COLORS.primary : COLORS.textSecondary,
      fontWeight: activeTab === tabName ? "bold" : "normal",
      fontSize: isMobile ? "0.9rem" : "1rem",
      padding: isMobile ? "0.8rem 0.5rem" : "1rem 1.5rem",
    }),
    [activeTab, isMobile]
  );

  if (isMobile) {
    return (
      <div style={styles.mobileContainer}>
        <style>
          {`
            .ui.dropdown .menu {
              max-height: none !important;
              overflow-y: visible !important;
            }
            .ui.dropdown .menu::-webkit-scrollbar {
              display: none !important;
            }
            .ui.dropdown .menu {
              -ms-overflow-style: none !important;
              scrollbar-width: none !important;
            }
          `}
        </style>
        <Dropdown
          fluid
          selection
          value={activeTab}
          onChange={(e, { value }) => onTabChange(value)}
          options={dropdownOptions}
          icon="dropdown"
          style={{ ...styles.dropdown }}
          trigger={
            <span style={{ color: COLORS.primary, fontWeight: "bold" }}>
              <Icon name={activeTabData.icon} />
              {activeTabData.label}
            </span>
          }
        />
      </div>
    );
  }

  return (
    <Menu pointing secondary style={styles.menu}>
      {tabs.map((tab) => (
        <Menu.Item
          key={tab.key}
          name={tab.name}
          active={activeTab === tab.name}
          onClick={() => onTabChange(tab.name)}
          style={getItemStyle(tab.name)}
        >
          <Icon name={tab.icon} />
          {tab.label}
        </Menu.Item>
      ))}
    </Menu>
  );
};

TabNavigation.propTypes = {
  activeTab: PropTypes.string.isRequired,
  onTabChange: PropTypes.func.isRequired,
  showLibraryCard: PropTypes.bool.isRequired,
};

export default TabNavigation;
